/**
 * CAOS-465 — the ONE ordering mechanism, on the host side, for a seam that stacks more than one
 * package's contribution: the edge gutter and Setup's contributed navigation groups both reach
 * for this rather than each inventing its own comparator.
 *
 * This is a deliberate MIRROR of `kernel/src/capabilities/seam-order.ts` — identical contract,
 * identical algorithm — not a second design. `@caos/host` deploys as its own dependency-free
 * Cloudflare Worker (see this package's `package.json`: no runtime `dependencies` at all) and the
 * two packages are not npm-workspace-linked (root `package.json`'s `workspaces` lists neither
 * `host` nor `kernel`), so host cannot `import` the kernel copy the way it could inside one
 * package. `host/src/kernel-contract.ts` already documents the same constraint for the kernel
 * runtime types it mirrors rather than imports; this follows that exact, already-established
 * precedent instead of inventing a new one. Keep the two files in lockstep — the kernel copy is
 * the canonical one; this one exists only because the deploy boundary forces a second file, not
 * because the ordering rule is allowed to drift between them.
 *
 * Before this module existed, the edge gutter had its OWN inline sort (`orderedAnchors` in
 * `surfaces/shell-frame.ts`, CAOS-450) and Setup's contributed rail groups had NONE at all
 * (`contributedSectionGroups` in `surfaces/setup-surface.ts`, CAOS-582, just walked
 * `meta.setupContributions` in the order the org's metadata listed the claims — install order in
 * every case that matters). Both now call this.
 */

/** What the comparator needs from a contribution: WHO made it, and the intent it declared. */
export interface SeamOrderKey {
  /** The package/component that made this contribution — the tie-break, never install order. */
  readonly sourceKey: string;
  /** The intent a contribution declared, lower first. Absent/null ⇒ sorts after every declared value. */
  readonly order?: number | null;
  /**
   * CAOS-851 — what settles two contributions from the SAME source at the SAME declared order: the
   * contribution's own name (Setup's contributed navigation passes the entry's route). Absent reads as
   * the empty string. Without it those two carried identical keys and the sort kept their arrival
   * position, which is the one order this function exists never to read.
   */
  readonly tieBreak?: string;
}

/**
 * An administrator's pinned arrangement for one seam: source keys, in the order the admin wants
 * them. A key the admin did not mention is unopinionated, not excluded — it keeps its place in
 * the deterministic default order and is appended after every pinned key. A pinned key that is no
 * longer contributing anything to this seam is silently ignored.
 *
 * Where an override like this is stored, resolved per-org, and edited by an administrator is
 * intentionally NOT this module's concern — see CAOS-732 (filed alongside this ticket, sprint 23) for the
 * persistence half. No seam on the host currently passes one; the parameter exists so a seam that
 * gains a resolved override later plugs it in here, not into a seam-local comparator.
 */
export interface SeamOrderOverride {
  readonly sourceKeys: readonly string[];
}

/**
 * Sort `contributions` for one seam.
 *
 * Default order: declared `order` ascending (via `keyOf`), ties broken by `sourceKey`, then by `tieBreak` (CAOS-851) —
 * locale-independent codepoint compare, the same guarantee a namespace already carries (unique,
 * and never renumbered by an uninstall/reinstall the way an array position would be). `keyOf` is
 * the ONLY thing this function reads off each element, which is what makes install order
 * unreadable BY CONSTRUCTION — there is nowhere in this function's signature for an install
 * sequence to enter.
 *
 * CAOS-735 — the tie-break is a plain `<`/`>` compare on the string (see `compareSourceKey`
 * below), never `String.prototype.localeCompare`. `injectionsFor`'s predecessor tie-broke with a
 * Postgres `ORDER BY source_key` in C locale (byte order); `.localeCompare()` invokes the
 * runtime's ICU collation instead, which agrees with C-locale byte order for plain ASCII (every
 * sourceKey in use today) but can silently disagree on accented/non-ASCII input, and can disagree
 * with itself between the kernel's and the host's JS runtime locale settings. Native `<`/`>`
 * compares by UTF-16 code unit — no collation table involved — which is deterministic regardless
 * of runtime locale and matches C-locale byte order.
 *
 * When `override` is given, its pinned `sourceKeys` win: each named key is placed in the order
 * the override lists, and every contribution the override has no opinion about keeps its
 * deterministic-default relative order, appended after the pinned ones (a newly installed package
 * with no pin lands relative to the existing arrangement without disturbing it).
 */
/**
 * CAOS-735 — the locale-independent tie-break: plain `<`/`>` on the two strings, never
 * `.localeCompare()`. JS string comparison operators compare by UTF-16 code unit, which never
 * invokes locale/collation rules, so this produces the SAME order regardless of the runtime's
 * locale setting — and, for the ASCII sourceKeys every package uses today, the same order the
 * predecessor's C-locale Postgres `ORDER BY source_key` produced.
 */
function compareSourceKey(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function orderSeamContributions<T>(
  contributions: readonly T[],
  keyOf: (contribution: T) => SeamOrderKey,
  override?: SeamOrderOverride,
): T[] {
  const compareDefault = (a: T, b: T): number => {
    const ka = keyOf(a);
    const kb = keyOf(b);
    const orderOf = (k: SeamOrderKey): number => k.order ?? Number.MAX_SAFE_INTEGER;
    const delta = orderOf(ka) - orderOf(kb);
    if (delta !== 0) return delta;
    const bySource = compareSourceKey(ka.sourceKey, kb.sourceKey);
    return bySource !== 0 ? bySource : compareSourceKey(ka.tieBreak ?? '', kb.tieBreak ?? '');
  };
  const deterministicDefault = [...contributions].sort(compareDefault);

  const pins = override?.sourceKeys ?? [];
  if (pins.length === 0) return deterministicDefault;

  const bySourceKey = new Map(deterministicDefault.map((c) => [keyOf(c).sourceKey, c] as const));
  const placed = new Set<string>();
  const result: T[] = [];
  for (const pinnedKey of pins) {
    const contribution = bySourceKey.get(pinnedKey);
    if (contribution !== undefined && !placed.has(pinnedKey)) {
      result.push(contribution);
      placed.add(pinnedKey);
    }
  }
  for (const contribution of deterministicDefault) {
    const key = keyOf(contribution).sourceKey;
    if (!placed.has(key)) {
      result.push(contribution);
      placed.add(key);
    }
  }
  return result;
}
